import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Histogram } from '../../types/stats';

export type EvaluationSelection = {
  Accepted: boolean;
  Rejected: boolean;
};

const EVALUATION_LABEL: Record<keyof EvaluationSelection, string> = {
  Accepted: 'Acc',
  Rejected: 'Rej',
};

const chartColors = [
  '#0ea5e9',
  '#22c55e',
  '#f97316',
  '#a855f7',
  '#14b8a6',
  '#f43f5e',
  '#3b82f6',
  '#facc15',
  '#6366f1',
  '#fb7185',
  '#fde047',
  '#34d399',
];

interface HistogramBarChartProps {
  histogram: Histogram;
  evaluations: EvaluationSelection;
  title: string;
}

export const HistogramBarChart = ({ histogram, evaluations, title }: HistogramBarChartProps) => {
  const option = useMemo(() => {
    const categories = histogram.bins.map((bin) => bin.label);
    const series: echarts.SeriesOption[] = [];
    const legendEntries: string[] = [];
    let colorIndex = 0;

    (Object.keys(evaluations) as Array<keyof EvaluationSelection>).forEach((evaluation) => {
      if (!evaluations[evaluation]) {
        return;
      }
      const commentMap = new Map<string, number[]>();
      histogram.bins.forEach((bin) => {
        const commentCounts = bin.counts[evaluation];
        Object.entries(commentCounts).forEach(([comment, value]) => {
          if (!commentMap.has(comment)) {
            commentMap.set(comment, Array(categories.length).fill(0));
          }
          const arr = commentMap.get(comment)!;
          const index = categories.indexOf(bin.label);
          arr[index] = value;
        });
      });

      commentMap.forEach((data, comment) => {
        const name = `${EVALUATION_LABEL[evaluation]} • ${comment}`;
      legendEntries.push(name);
      series.push({
        name,
        type: 'bar',
        stack: evaluation,
          emphasis: { focus: 'series' },
          data,
          barWidth: 8,
          itemStyle: { color: chartColors[colorIndex % chartColors.length] },
        });
        colorIndex += 1;
      });
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      legend: {
        data: legendEntries,
        type: 'scroll',
        top: 0,
        left: 'center',
        orient: 'horizontal',
        selector: [
          { type: 'all', title: 'Show All' },
          { type: 'inverse', title: 'Inverse' },
        ],
      },
      grid: { left: 80, right: 30, top: 60, bottom: 80 },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { rotate: 60 },
        name: histogram.column,
        nameLocation: 'middle',
        nameGap: 60,
      },
      yAxis: {
        type: 'value',
        name: 'Number of indications',
      },
      dataZoom: [
        { type: 'slider', start: 0, end: 100 },
        { type: 'inside', start: 0, end: 100 },
      ],
      series,
    } as echarts.EChartsOption;
  }, [histogram, evaluations]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-semibold text-blueprint-blue">{title}</h3>
      </div>
      <ReactECharts option={option} style={{ height: 450 }} />
    </div>
  );
};
